import { Alert, AlertProps, useMantineTheme } from '@mantine/core';
import { IconInfoCircle, IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { useState } from 'react';

export interface AfaAlertProps extends Omit<AlertProps, 'color' | 'variant'> {
  /**
   * Tipo de alerta que determina o color e o ícone
   * @default 'info'
   */
  type?: 'info' | 'success' | 'warning' | 'error' | 'default';
  /**
   * Estilo visual da alerta (aparência)
   * @default 'light'
   */
  variant?: 'filled' | 'light' | 'outline' | 'transparent' | 'white' | 'default';
  /**
   * Título da alerta
   */
  title?: React.ReactNode;
  /**
   * Se deve mostrar o ícone
   * @default true
   */
  withIcon?: boolean;
  /**
   * Se a alerta pode ser fechada
   * @default false
   */
  withCloseButton?: boolean;
  /**
   * Callback quando a alerta é fechada
   */
  onClose?: () => void;
  /**
   * Espaçamento entre o título e o conteúdo
   * @default 'xs' (var(--mantine-spacing-xs))
   */
  titleGap?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  /**
   * Cores de fundo personalizadas para variante light por tipo
   */
  lightBackgroundColors?: {
    info?: string;
    success?: string;
    warning?: string;
    error?: string;
  };
}

const AfaAlert = ({ 
  type = 'info',
  variant = 'light',
  title,
  children, 
  withIcon = true,
  withCloseButton = false,
  onClose,
  titleGap,
  lightBackgroundColors,
  ...props 
}: AfaAlertProps) => {
  const theme = useMantineTheme();
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  // Se o alerta foi fechado, não renderizar nada
  if (!isVisible) {
    return null;
  }

  const getAlertConfig = () => {
    switch (type) {
      case 'success':
        return {
          color: 'green',
          lightBgColor: lightBackgroundColors?.success || 'EFF9F0',
          icon: <IconCheck/>,
        };
      case 'warning':
        return {
          color: 'yellow',
          lightBgColor: lightBackgroundColors?.warning || '#FDF6EB',
          icon: <IconAlertCircle />,
        };
      case 'error':
        return {
          color: 'red',
          lightBgColor: lightBackgroundColors?.error || '#FAEBEA',
          icon: <IconX/>,
        };
      case 'default':
        return {
          color: theme.other.black,
          lightBgColor: undefined,
          icon: <IconInfoCircle/>,
        };
      case 'info':
      default:
        return {
          color: 'blue',
          lightBgColor: lightBackgroundColors?.info || '#EDF2FD',
          icon: <IconInfoCircle/>,
        };
    }
  };

  const config = getAlertConfig();

  // Função para obter o valor do espaçamento
  const getGapValue = () => {
    // Se não for especificado titleGap, usa '0' como padrão (sem espaço)
    const defaultGap = 0;
    const gap = titleGap ?? defaultGap;
    
    if (typeof gap === 'number') return `${gap}px`;
    return `var(--mantine-spacing-${gap})`;
  };

  const alertProps: AlertProps = {
    icon: withIcon ? config.icon : undefined,
    color: config.color,
    variant: variant,
    title,
    radius: "sm",
    withCloseButton,
    ...props,
  };

  if (withCloseButton) {
    alertProps.onClose = handleClose;
  }

  // Aplicar background customizado via style inline se necessário
  if (variant === 'light' && config.lightBgColor) {
    alertProps.style = {
      ...alertProps.style,
      backgroundColor: config.lightBgColor,
    };
  }

  // Sempre sobrescrever o espaçamento (usa o padrão se não for especificado)
  alertProps.styles = {
    ...alertProps.styles,
    wrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      marginRight: 'var(--mantine-spacing-sm)', // 12px de espaçamento entre ícone e texto
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      '& svg': {
        width: '24px !important',
        height: '24px !important',
      }
    },
    body: {
      gap: getGapValue(),
    }
  };

  return (
    <Alert {...alertProps}>
      {children}
    </Alert>
  );
};export default AfaAlert;
